import { type ReactElement, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLocales } from '@/hooks/useLocales';
import { useLocaleStorage } from '@/hooks/useLocaleStorage';
import { usePageTitle } from '@/hooks/usePageTitle';
import { LocalePicker } from '@/components/shared/LocalePicker/LocalePicker';
import { clearUHFCache } from '@/services/uhfService';
import { apiService } from '@/services/apiService';
import { useTranslations } from '@/hooks/useTranslations';
import styles from './LocalePickerPage.module.scss';

/**
 * Locale Picker Page
 * Allows users to view and select their preferred locale
 */
export function LocalePickerPage(): ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const { locales, loading, error } = useLocales();
  const { saveLocale } = useLocaleStorage();
  const { t } = useTranslations();

  usePageTitle(t("localePicker.selectLanguageTitle"));

  const handleSelectLocale = useCallback((localeCode: string): void => {
    const success = saveLocale(localeCode);

    if (!success) {
      console.error('Failed to save locale preference');
      return;
    }

    clearUHFCache();

    const stateFrom = (location.state as { from?: string })?.from;
    const storedPrevious = sessionStorage.getItem('previousRoute') || undefined;
    const candidate = stateFrom || storedPrevious;
    const previousContentKey = sessionStorage.getItem('previousContentKey') || undefined;
    const previousContentPath = sessionStorage.getItem('previousContentPath') || undefined;

    const fallbackNavigate = (): void => {
      let target: string;
      if (candidate && candidate !== '/change-locale') {
        const match = candidate.match(/^\/[^/]+(\/.*)?$/);
        if (match) {
          const rest = match[1] ?? '';
          target = `/${localeCode}${rest}`;
        } else {
          target = `/${localeCode}`;
        }
      } else {
        target = `/${localeCode}`;
      }

      navigate(target, { replace: true });
    };

    // Preferred: navigate to the localized URL for the same content node.
    if (previousContentKey && candidate && previousContentPath && candidate === previousContentPath) {
      void (async (): Promise<void> => {
        try {
          const page = await apiService.getPageById(previousContentKey, localeCode);

          if (page && typeof page === 'object' && 'code' in page) {
            fallbackNavigate();
            return;
          }

          const resolvedUrl = (page as { url?: string }).url;
          if (resolvedUrl) {
            navigate(resolvedUrl, { replace: true });
            return;
          }

          fallbackNavigate();
        } catch {
          fallbackNavigate();
        }
      })();

      return;
    }

    // Fallback: best-effort rewrite of the current path.
    fallbackNavigate();
  }, [saveLocale, navigate, location.state]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.stateContainer}>
          <div className={styles.loading}>
            <p>{t("localePicker.loadingLanguages")}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.stateContainer}>
          <div className={styles.error} role="alert" aria-live="polite">
            <h1>{t("localePicker.unableToLoadTitle")}</h1>
            <p>{t("localePicker.unableToLoadMessage")}</p>
          </div>
        </div>
      </div>
    );
  }

  if (locales.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.stateContainer}>
          <div className={styles.error} role="status" aria-live="polite">
            <h1>{t("localePicker.noLocalesTitle")}</h1>
            <p>{t("localePicker.noLocalesAvailable")}</p>
            <p>{t("localePicker.noLocalesHelp")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <noscript>
        <div className={styles.noScript}>
          <h1>Select Your Language</h1>
          <p>JavaScript is disabled. Basic functionality available below:</p>
          <form method="GET" action="/">
            {locales.map((locale) => (
              <button 
                key={locale.code} 
                type="submit" 
                name="locale" 
                value={locale.code}
                className={styles.noScriptButton}
                aria-label={t("localePicker.selectLocale", { localeName: locale.name })}
              >
                {locale.flagIcon} {locale.name}
              </button>
            ))}
          </form>
        </div>
      </noscript>
      <LocalePicker locales={locales} onSelect={handleSelectLocale} />
    </div>
  );
}
